import { observable, action, makeObservable } from "mobx";
import getEnvironDomain from "../utils/getEnvironDomain";

import Queue from "./models/Queue";
import Timer from "./models/Timer";
import PopupElement from "./models/PopupElement";

import { popupsData, mapIdTemplate } from "./popupData/popupData";

/** Константы для сортировки */
const prioritySortConsts = {
  high: 2,
  medium: 1,
  low: -1,
};

const baseUrl = getEnvironDomain();

const alwaysEnabledUrls = [
  `test.${baseUrl}:3000`,
  `test.${baseUrl}:3301`,
  `test.${baseUrl}:3302`,
];

const isEnableForCurrentPage = (popupData) => {
  if (!popupData) return false;

  if (
    !popupData.enableURLs ||
    alwaysEnabledUrls.includes(window.location.host)
  ) {
    return true;
  }

  let result = false;
  const url = document.URL.replace(/\?.*$/, "").replace(/\/$/, "");

  for (let i = 0; i < popupData.enableURLs.length; i++) {
    let enableURL = popupData.enableURLs[i].replace(/\/$/, "");

    if (enableURL[enableURL.length - 1] === "*") {
      enableURL = enableURL.replace(/\*$/, "").replace(/\/$/, "");
      if (url.includes(enableURL)) {
        result = true;
        break;
      }
    } else if (url === enableURL) {
      result = true;
      break;
    }
  }

  return result;
};

class PopupController {
  constructor(rootStore) {
    makeObservable(this, {
      bottomLeftPopup: observable,
      centerPopup: observable,
      bottomRightPopup: observable,
      topRightPopup: observable,
      topLeftPopup: observable,
      setValueToPosition: action,
    });

    this.rootStore = rootStore;

    this.queue = new Queue(
      (aPopup, bPopup) =>
        prioritySortConsts[aPopup.priority] -
        prioritySortConsts[bPopup.priority]
    );

    /** Это локальные очереди на каждую из отображаемых позиций */
    this.popupShowCase = {
      center: new Queue(),
      bottomLeft: new Queue(),
      bottomRight: new Queue(),
      topRight: new Queue(),
      topLeft: new Queue(),
    };

    this.timer = new Timer(2000, this.getNextFromQueue);

    if (window) {
      // Методы для внешнего запуска попапов
      window.popups = {
        create: (data) => new PopupElement(data),
        show: this.addPopupToQueue,
        close: this.handlePopupClose,
      };

      // Информация о текущем состоянии попапов
      window.popupsTestInfo = () => {
        console.log({
          mapIdTemplate,
          popupsData,
        });
      };

      // Функция для тестирования попапа
      window.showPopupById = (id, options) => {
        const func = () => {
          const newPopup = new PopupElement({
            id,
            name: "Тестовый попап",
            eventType: "internal",
            priority: "high",
            position: popupsData[id].position || "bottomRight",
            templateName: mapIdTemplate[id],
            templateData: popupsData[id],
            custom: false,
            finishDate: popupsData[id].finishDate,
            startDate: popupsData[id].startDate,
          });

          // Для тестирования отключаем даты, чтобы можно было протестировать еще не начатые попапы
          if (options?.withoutDates) {
            newPopup.clearDates();
          }

          this.addPopupToQueue(newPopup);
        };

        if (options?.trigger) {
          this.showByTrigger[options.trigger] = func;
        } else {
          func();
        }
      };
    }
  }

  /**
   * Эти переменные нужны для ререндера при переключении между попапами. Метод отображения изменяет данные и в компоненте благораря ререндеру изменяется отображение.
   *
   * @memberof PopupController
   */
  bottomLeftPopup = null;

  centerPopup = null;

  bottomRightPopup = null;

  topRightPopup = null;

  topLeftPopup = null;

  /** Показываем лишь один попап за сессию */
  isPopupWasOpened = false;

  showByTrigger = {};

  /**
   * Этот метод будет сложнее. Так как сейчас нет ни попапов с подобным функционалом, ни описания функциональности.
   * Сейчас просто забираем попап из общей очереди и кладем в очередь по местоположению.
   */
  getNextFromQueue = () => {
    const popup = this.queue.get();

    if (!this.queue.length) {
      this.timer.stop();
    }

    this.showPopup(popup);
  };

  popupControllerMessageReceiver = (data) => {
    if (data && data.triggerName) {
      const showPopupFunction = this.showByTrigger[data.triggerName];

      if (showPopupFunction) {
        showPopupFunction();
      }
    }
  };

  getPopupFeatures = async () => {
    const { enabler } = this.rootStore;
    const features = await enabler.getFeaturesByIdList(Object.keys(popupsData));
    const todayTime = new Date().getTime();

    if (features) {
      for (let i = 0; i < features.length; i++) {
        const feature = features[i];

        if (
          feature &&
          mapIdTemplate[feature.id] &&
          !feature["is_viewed"] &&
          feature["is_enabled"] &&
          isEnableForCurrentPage(popupsData[feature.id])
        ) {
          const currentId = feature.id;

          const showPopupFunction = () => {
            const newPopup = new PopupElement({
              id: currentId,
              name: feature.title,
              eventType: "internal",
              priority: "high",
              position: popupsData[currentId].position || "bottomRight",
              templateName: mapIdTemplate[currentId],
              templateData: popupsData[currentId],
              custom: false,
              finishDate: popupsData[currentId].finishDate,
              startDate: popupsData[currentId].startDate,
            });

            const finishDate = newPopup.finishDate;
            const startDate = newPopup.startDate;

            if (
              ((!!finishDate && new Date(finishDate).getTime() >= todayTime) ||
                !finishDate) &&
              ((!!startDate && new Date(startDate).getTime() <= todayTime) ||
                !startDate)
            ) {
              this.addPopupToQueue(newPopup);
            }
          };

          if (popupsData[currentId] && popupsData[currentId].triggerMessage) {
            popupsData[currentId].triggerMessage.forEach((trigger) => {
              this.showByTrigger[trigger] = showPopupFunction;
            });
          } else {
            showPopupFunction();
          }

          // Прерываем работу, чтобы за одну сессию был показан только 1 попап
          break;
        }
      }
    }
  };

  /** Функция для добавления попапа в систему, требует объект класса PopupElement */
  addPopupToQueue = (popup) => {
    if (this.isPopupWasOpened) {
      return;
    }

    const finishDate = popup?.templateData?.finishDate;
    const startDate = popup?.templateData?.startDate;
    const todayTime = new Date().getTime();

    if (
      (finishDate && new Date(finishDate).getTime() < todayTime) ||
      (startDate && new Date(startDate).getTime() > todayTime)
    ) {
      return;
    }

    this.isPopupWasOpened = true;

    if (popup.priority === "important") {
      this.showPopup(popup);
    } else {
      this.queue.put(popup);
      if (!this.timer.isPlaying) {
        this.timer.play();
      }
    }
  };

  /*
   * Этот метод либо сразу отображает попап, если на выбранной позиции ничего нет, либо добавляет в очередь для этой позиции
   */
  showPopup = (popup) => {
    if (
      this.popupShowCase[popup.position].length === 0 &&
      !this[`${popup.position}Popup`]
    ) {
      this.renderPopup(popup);
    } else {
      this.popupShowCase[popup.position].put(popup);
    }
  };

  /**
   * Этот метод должен дергаться при закрытии попапа чтобы освободить очередь и отобразить следующего.
   */
  handlePopupClose = (popup) => {
    const { enabler } = this.rootStore;

    if (popup && popup.closeCallback) {
      popup.closeCallback();
    }

    if (!popup.custom) {
      enabler.markAsViewed(popup.id);
    }

    this.setValueToPosition(popup.position, undefined);

    // Таймаут чтобы попапы переключались с небольшой задержкой
    setTimeout(() => {
      const nextPopup = this.popupShowCase[popup.position].get();

      if (nextPopup) {
        this.renderPopup(nextPopup);
      }
    }, 1000);
  };

  setValueToPosition = (position, value) => {
    if (position === "center") {
      this.centerPopup = value;
    } else if (position === "bottomLeft") {
      this.bottomLeftPopup = value;
    } else if (position === "bottomRight") {
      this.bottomRightPopup = value;
    } else if (position === "topRight") {
      this.topRightPopup = value;
    } else if (position === "topLeft") {
      this.topLeftPopup = value;
    }
  };

  /**
   * Метод дергает колбэк для показа попапа и занимает место на экране.
   * Также он изменяет observable поле, чтобы в компоненте можно было словить ререндер.
   */
  renderPopup = (popup) => {
    if (popup && popup.openCallback) {
      popup.openCallback();
    }

    this.setValueToPosition(popup.position, popup);
  };
}

export default PopupController;
